import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { ServicedMeasurement } from "../../models/ServicedMeasurement";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { Weighbridge } from "../../weight-bridge/Weighbridge";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { WeighbridgeInput } from "../../components/WeighbridgeInput/WeighbridgeInput";
import { getWeighbridge } from "../../weight-bridge/Weighbridge";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getServicedMeasurementTemplate } from "../../document-templates/ServicedMeasurementTemplate";
import { activeUser } from "../../services/AuthService";
import {
  getOneServicedMeasurement,
  updateOneServicedMeasurement,
  addNewServicedMeasurement,
} from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllPartners } from "../../services/PartnerService";
import "./ServicedMeasurementPage.scss";

export interface ServicedMeasurementPageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const ServicedMeasurementPage = (p: ServicedMeasurementPageProps) => {
  const { buttonSaveStyle, buttonResetStyle } = p;

  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef<any>(null);
  const isCreateMode = (location.state as any)?.mode === "create";
  const didInitRef = useRef(false);

  const { state, serialPortSet, serialPortValueSet } = useApplicationContext();

  const [updating, setUpdating] = useState<boolean>(false);
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [newServicedMeasurement, setNewServicedMeasurement] =
    useState<ServicedMeasurement>({
      id: id ?? "",
      warehouse: {} as Warehouse,
      partner: {} as Partner,
      warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
      documentId: undefined,
      date: new Date(),
      brutto: undefined,
      tara: undefined,
      netto: undefined,
      product: undefined,
      driverName: undefined,
      vehicleNumber: undefined,
      note: "",
    });

  const [processing, setProcessing] = useState<boolean>(false);
  const [pdfData, setPdfData] = useState<any>(
    getServicedMeasurementTemplate(newServicedMeasurement)
  );

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, pResp] = await Promise.all([
      getAllWarehouses(),
      getAllPartners(),
    ]);
    [wResp, pResp].forEach(ensureSuccess);

    setWarehousesApi(wResp?.data?.result ?? []);
    setPartnersApi(pResp?.data?.result ?? []);
  };

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const run = async () => {
      setProcessing(true);
      try {
        await refreshLookups();

        if (id && !isCreateMode) {
          const resp: AxiosResponse = await getOneServicedMeasurement(id);
          if (resp?.data?.status === "ERROR") {
            throw new Error(resp?.data?.message ?? "Greška pri učitavanju");
          }

          const document = resp?.data?.result;
          if (document) {
            if (document.date) document.date = new Date(document.date);
            if (!document.warehouseman) {
              document.warehouseman = `${activeUser.firstName} ${activeUser.lastName}`;
            }
            setNewServicedMeasurement(document);
            setUpdating(true);
          }
        }
      } catch (e: any) {
        toast.current?.show({
          severity: "error",
          detail: e?.message ?? String(t("Greška pri učitavanju podataka")),
          life: 3500,
        });
      } finally {
        setProcessing(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let copyOfNewServicedMeasurement = cloneDeep(newServicedMeasurement);
    copyOfNewServicedMeasurement.netto =
      (newServicedMeasurement?.brutto ?? 0) -
      (newServicedMeasurement?.tara ?? 0);
    setNewServicedMeasurement(copyOfNewServicedMeasurement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newServicedMeasurement.brutto, newServicedMeasurement.tara]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getServicedMeasurementTemplate(newServicedMeasurement);
    setPdfData(newPdf);
  }, [newServicedMeasurement]);

  //* -------------- WEIGHBRIDGE START ----------------
  const [weighbridge, setWeighbridge] = useState<Weighbridge>();
  const [readableStreamClosed, setReadableStreamClosed] = useState<any>();
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null);
  const readableStreamClosedRef = useRef<Promise<void> | null>(
    Promise.resolve()
  );

  const disconnectSerial = async () => {
    try {
      await readerRef.current?.cancel();
      await readerRef.current?.releaseLock();
      readerRef.current = null;

      await readableStreamClosedRef.current?.catch(() => {});
      readableStreamClosedRef.current = null;

      if (state.serialPort) await state.serialPort.close();
    } catch (e) {
      console.error(e);
    } finally {
      serialPortSet(undefined);
    }
  };

  const connectSerial = async (_weighbridge: Weighbridge) => {
    try {
      let _port;

      if (state.serialPort && state.serialPort.readable) {
        _port = state.serialPort;
      } else {
        _port = await window.navigator.serial.requestPort();
        serialPortSet(_port);
        await _port.open({ baudRate: _weighbridge.baudRate });
      }

      const decoder = new TextDecoderStream();
      const inputDone = _port.readable.pipeTo(decoder.writable);
      const lineReadableStream = new TransformStream(
        _weighbridge.getLineBreakTransformer()
      );
      const newReadableStreamClosed =
        decoder.readable.pipeThrough(lineReadableStream);

      const lineReader = lineReadableStream.readable.getReader();

      setReadableStreamClosed(newReadableStreamClosed);

      readerRef.current = lineReader;
      readableStreamClosedRef.current = inputDone;

      let previousValue = "";
      while (true) {
        if (!_port || !_port.readable) continue;

        const { value, done } = await lineReader.read();
        if (done) {
          break;
        }

        if (previousValue !== value) {
          serialPortValueSet(value);
        }

        previousValue = value;
      }
    } catch (e) {
      console.error(e);
    } finally {
      await disconnectSerial();
    }
  };

  useEffect(() => {
    if (!newServicedMeasurement.warehouse?.weighbridgeModel) return;
    const _weighbridge = getWeighbridge(
      newServicedMeasurement.warehouse.weighbridgeModel
    );

    if (!_weighbridge) return;
    setWeighbridge(_weighbridge);

    connectSerial(_weighbridge);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newServicedMeasurement.warehouse]);
  //* --------------- WEIGHBRIDGE END -----------------

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const onSubmit = async () => {
    let copyOfNewServiceMeasurement = cloneDeep(newServicedMeasurement);
    copyOfNewServiceMeasurement.netto =
      (newServicedMeasurement?.brutto ?? 0) -
      (newServicedMeasurement?.tara ?? 0);
    if (!copyOfNewServiceMeasurement.warehouseman) {
      copyOfNewServiceMeasurement.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
    }
    try {
      let resp: AxiosResponse;
      if (updating) {
        resp = await updateOneServicedMeasurement(
          newServicedMeasurement.id,
          copyOfNewServiceMeasurement
        );
      } else {
        resp = await addNewServicedMeasurement(copyOfNewServiceMeasurement);
      }
      ensureSuccess(resp);

      toast?.current?.show({
        severity: "success",
        detail: t(`Uspešno popunjeno uslužno merenje`),
        life: 2000,
      });

      navigate(`/serviced-measurement`, { state: { refresh: true } });
    } catch (e: any) {
      toast?.current?.show({
        severity: "error",
        detail: e?.message ?? t("Greška pri snimanju"),
        life: 3500,
      });
    }
  };

  const onError = () => {
    toast?.current?.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetForm = () => {
    setNewServicedMeasurement({
      ...newServicedMeasurement,
      warehouse: {} as Warehouse,
      partner: {} as Partner,
      warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
      date: new Date(),
      brutto: undefined,
      tara: undefined,
      netto: undefined,
      product: undefined,
      driverName: undefined,
      vehicleNumber: undefined,
      note: "",
    });
  };

  const handleNewServicedMeasurement = (prop: string, value: any) => {
    setNewServicedMeasurement((newServicedMeasurement) => ({
      ...newServicedMeasurement,
      [prop]: value,
    }));
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewServicedMeasurement = cloneDeep(newServicedMeasurement);
    copyOfNewServicedMeasurement.warehouse = warehousesApi.find(
      (item) => item.id == value
    ) as Warehouse;
    setNewServicedMeasurement(copyOfNewServicedMeasurement);
  };

  const handlePartnerChange = (prop: string, value: any) => {
    let copyOfNewServicedMeasurement = cloneDeep(newServicedMeasurement);
    copyOfNewServicedMeasurement.partner = partnersApi.find(
      (item) => item.id == value
    ) as Partner;
    setNewServicedMeasurement(copyOfNewServicedMeasurement);
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      {processing ? (
        <ProgressSpinner />
      ) : (
        <div className="form-wrapper">
          <div className="form-header">
            <div className="header-title">
              <i
                className="bi bi-file-text"
                style={{ fontSize: 16, marginRight: 6 }}
              ></i>
              <span className="header-title">
                {`${t("Uslužno merenje broj")}:`}{" "}
                {newServicedMeasurement?.measurementNumberFormatted ??
                  newServicedMeasurement.documentId}
              </span>
            </div>
            <div className="header-buttons">
              <ButtonInput
                addStyle={{
                  border: "1px solid #01a453",
                  background: "#ffffff",
                  color: "#01a453",
                  margin: "0 .5rem",
                }}
                labelText={t("Sva uslužna merenja")}
                icon="bi bi-arrow-left-square"
                onButtonClick={() => {
                  navigate(`/serviced-measurement`);
                }}
              />
              <ButtonInput
                addStyle={{
                  border: "1px solid #01a453",
                  background: "#ffffff",
                  color: "#01a453",
                  margin: "0 .5rem",
                }}
                labelText={""}
                icon="bi bi-file-earmark-pdf"
                onButtonClick={() => {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                }}
              />
              <ButtonInput
                addStyle={{
                  border: "1px solid #01a453",
                  background: "#ffffff",
                  color: "#01a453",
                }}
                labelText={""}
                icon="bi bi-printer"
                onButtonClick={() => {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                }}
              />
            </div>
          </div>
          <div className="form-body">
            <div className="form-input-fields">
              <DropdownInput
                id="warehouse"
                value={newServicedMeasurement?.warehouse?.id}
                onValueChange={handleWarehouseChange}
                optionValue="id"
                optionLabel="name"
                options={warehousesApi.filter((item) => item.virtual !== true)}
                filter={false}
                labelText={`${t("Magacin")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DateInput
                id="date"
                value={newServicedMeasurement.date as Date}
                onValueChange={handleNewServicedMeasurement}
                labelText={`${t("Datum i vreme")} *`}
                placeholderText={t("Izaberite datum i vreme")}
                icon="bi bi-calendar-check"
                showTime={true}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <TextInputWithIcon
                id="warehouseman"
                value={newServicedMeasurement.warehouseman ?? ""}
                onValueChange={handleNewServicedMeasurement}
                labelText={`${t("Magacioner")} *`}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <DropdownInput
                id="partner"
                value={newServicedMeasurement?.partner?.id}
                onValueChange={handlePartnerChange}
                optionValue="id"
                optionLabel="name"
                options={partnersApi}
                filter={true}
                labelText={`${t("Klijent")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <TextInputWithIcon
                id="vehicleNumber"
                value={newServicedMeasurement.vehicleNumber ?? ""}
                onValueChange={handleNewServicedMeasurement}
                labelText={`${t("Broj tablica")} *`}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={t("Unesite broj tablica vozila")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInputWithIcon
                id="driverName"
                value={newServicedMeasurement.driverName ?? ""}
                onValueChange={handleNewServicedMeasurement}
                labelText={`${t("Ime vozača")} *`}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={t("Unesite ime vozača")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <TextInputWithIcon
                id="product"
                value={newServicedMeasurement.product ?? ""}
                onValueChange={handleNewServicedMeasurement}
                labelText={`${t("Proizvod")} *`}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={t("Unesite proizvod")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <div></div>
              <WeighbridgeInput
                id="brutto"
                value={(newServicedMeasurement.brutto as number) ?? ""}
                onValueChange={handleNewServicedMeasurement}
                labelText={`${t("Bruto")} *`}
                icon="bi bi-minecart-loaded"
                iconAlign="right"
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter a number",
                  },
                }}
                weighbridge={weighbridge}
              />
              <WeighbridgeInput
                id="tara"
                value={(newServicedMeasurement.tara as number) ?? ""}
                onValueChange={handleNewServicedMeasurement}
                labelText={`${t("Tara")} *`}
                icon="bi bi-minecart"
                iconAlign="right"
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter a number",
                  },
                }}
                weighbridge={weighbridge}
              />
              <div className="note-field">
                <TextAreaInput
                  id="note"
                  value={newServicedMeasurement.note as string}
                  onValueChange={handleNewServicedMeasurement}
                  rows={1}
                  cols={50}
                  labelText={t("Napomena")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
              </div>
              <div></div>
              <div className="footer-buttons">
                <ButtonInput
                  labelText={t("Sačuvaj")}
                  onButtonClick={handleSubmit(onSubmit, onError)}
                  addStyle={addButtonSaveStyle}
                />
                <ButtonInput
                  labelText={t("Resetuj")}
                  onButtonClick={handleResetForm}
                  addStyle={addButtonResetStyle}
                />
              </div>
            </div>
            <div className="documents-preview">
              <PdfPreview
                pdfDefinition={pdfData}
                downloadDocument={downloadPdfDocument}
                printDocument={printPdfDocument}
                documentName={`Uslužno_merenje_${
                  newServicedMeasurement.documentId ?? ""
                }`}
              />
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};
